// OB Builder console — shared types + form option lists.
//
// ObScenario mirrors supabase/functions/_shared/ob-core/scenario.ts (the wire
// contract). Option lists carry the OB enum VALUES the admin picks, with readable
// labels — presentation data, not logic (logic lives in fieldRules/validate).

export interface ObScenario {
  loanPurpose?: string;
  loanType?: string;
  lienType?: string;
  baseLoanAmount?: number;
  firstLienAmount?: number;
  secondLienAmount?: number;
  loanTermOb?: string;
  amortizationType?: string;
  armFixedTermOb?: string;
  interestOnly?: boolean;
  interestOnlyTermMonths?: number;
  cashOutAmount?: number;
  buydown?: string;
  aus?: string;
  borrowerPaidMI?: string;
  reducedMI?: boolean;
  prepaymentPenalty?: string;
  productTypes?: string[];
  expandedApprovalLevel?: string;
  lockDays?: number;
  includeLOCompensationInPricing?: string;
  propertiesFinanced?: number;
  customerInternalId?: string;
  fico?: number;
  dti?: number;
  citizenship?: string;
  selfEmployed?: boolean;
  waiveEscrows?: boolean;
  firstTimeHomeBuyer?: boolean;
  monthsReserves?: number;
  veteranType?: string;
  borrowerFirstName?: string;
  borrowerLastName?: string;
  borrowerStreetAddress?: string;
  appraisedValue?: number;
  salesPrice?: number;
  occupancy?: string;
  propertyType?: string;
  propertyState?: string;
  propertyCounty?: string;
  propertyZip?: string;
  numberOfUnits?: string;
  numberOfStories?: number;
  propertyCity?: string;
  propertyStreetAddress?: string;
  incomeVerificationType?: string;
  dscrRatio?: number;
  housingEvent?: string;
  housingEventSeasoning?: string;
  bankruptcy?: string;
  bankruptcyOutcome?: string;
  bankruptcySeasoning?: string;
  mortgageLates?: string;
  debtConsolidation?: boolean;
  uniqueProperty?: boolean;
  compPercent?: number;
  targetRate?: number;
}

export type ObBuilderStatus =
  | 'validated'
  | 'validation_error'
  | 'priced'
  | 'no_products'
  | 'products_without_priceable_values'
  | 'ob_error'
  | 'ob_timeout'
  | 'ob_parse_error'
  | 'rate_limited';

export type ObBuilderIdentityMode = 'company' | 'admin_actor';

export interface ObBuilderPricingIdentity {
  mode: ObBuilderIdentityMode;
  businessChannelId: string;
  originatorId: string;
}

export interface EligibleProduct {
  productName?: string;
  investor?: string;
  investorId?: number;
  rate?: number;
  price?: number;
  apr?: number;
  principalAndInterest?: number;
  monthlyMI?: number;
  totalPayment?: number;
  lockPeriod?: number;
  closingCost?: number;
  loCompensationPercent?: number | null;
  loCompensationDollars?: number | null;
}

export interface ObBuilderResponse {
  runId: string | null;
  persisted?: boolean;
  persistError?: string | null;
  cached?: boolean;
  mode: 'validate' | 'live';
  status: ObBuilderStatus;
  errors: string[];
  fieldRules?: { hidden: string[]; required: string[]; notes: string[] };
  pricingIdentity?: ObBuilderPricingIdentity;
  request?: Record<string, unknown> | null;
  response?: { httpStatus: number | null; searchId: string | null; raw: unknown } | null;
  products?: EligibleProduct[];
  best?: { byRateBucket: EligibleProduct | null; byPriceFloor: EligibleProduct | null } | null;
  error?: { code?: string; message: string } | null;
  requestHash?: string;
  durationMs?: number;
  // transport-level failure envelope (non-2xx from the edge fn)
  message?: string;
}

export interface Opt { value: string; label: string; }
const opt = (value: string, label?: string): Opt => ({ value, label: label ?? value });

// ── OB enum option lists (values = OB enums; labels = readable) ──────────────
export const LOAN_PURPOSES: Opt[] = [
  opt('Purchase'), opt('RefiRateTermLimitedCO', 'Rate/Term Refi'), opt('RefiCashout', 'Cash-Out Refi'),
  opt('FHAStreamlineRefi', 'FHA Streamline'), opt('VARateReduction', 'VA IRRRL'), opt('SimpleRefinance', 'Simple Refi')
];
export const LOAN_TYPES: Opt[] = [
  opt('Conventional'), opt('Conforming'), opt('NonConforming', 'Non-Conforming'), opt('FHA'), opt('VA'),
  opt('HELOC', 'HELOC (experiment)'), opt('USDARuralHousing', 'USDA')
];
export const OCCUPANCIES: Opt[] = [opt('PrimaryResidence', 'Primary'), opt('SecondHome', 'Second Home'), opt('InvestmentProperty', 'Investment')];
export const PROPERTY_TYPES: Opt[] = [
  opt('SingleFamily', 'Single Family'), opt('Condo'), opt('Townhouse'), opt('PUD'), opt('DetachedCondo', 'Detached Condo'),
  opt('NonWarrantableCondo', 'Non-Warrantable Condo'), opt('Condotel'), opt('ManufacturedSingleWide', 'Mfg Single-Wide'),
  opt('ManufacturedDoubleWide', 'Mfg Double-Wide'), opt('Modular'), opt('Coop', 'Co-op'), opt('Timeshare')
];
export const PRODUCT_TYPES: Opt[] = [
  opt('StandardProducts', 'Standard'), opt('ExpandedGuidelines', 'Expanded Guidelines'), opt('AffordableProducts', 'Affordable'),
  opt('HUDspecialty', 'HUD Specialty'), opt('RenovationAndRehabilitation', 'Reno & Rehab'), opt('StudentLoanCORefi', 'Student Loan CO Refi'),
  opt('USDAStreamline', 'USDA Streamline')
];
export const LOAN_TERMS: Opt[] = [
  opt('TenYear', '10 yr'), opt('FifteenYear', '15 yr'), opt('TwentyYear', '20 yr'),
  opt('TwentyFiveYear', '25 yr'), opt('ThirtyYear', '30 yr'), opt('FortyYear', '40 yr')
];
export const LIEN_TYPES: Opt[] = [opt('First'), opt('Second')];
export const AMORTIZATION_TYPES: Opt[] = [opt('Fixed'), opt('ARM')];
export const ARM_FIXED_TERMS: Opt[] = [
  opt('OneMonth', '1mo'), opt('ThreeMonth', '3mo'), opt('SixMonth', '6mo'), opt('OneYear', '1/6'), opt('TwoYear', '2/6'),
  opt('ThreeYear', '3/6'), opt('FiveYear', '5/6'), opt('SixYear', '6/6'), opt('SevenYear', '7/6'), opt('TenYear', '10/6'), opt('FifteenYear', '15/6')
];
export const AUS_OPTIONS: Opt[] = [opt('NotSpecified', 'Not Specified'), opt('DU'), opt('LP'), opt('InvestorAUS', 'Investor AUS'), opt('ManualTraditional', 'Manual')];
export const BUYDOWNS: Opt[] = [opt('None'), opt('OneZero', '1-0'), opt('OneOne', '1-1'), opt('TwoOne', '2-1'), opt('ThreeTwoOne', '3-2-1')];
export const MI_OPTIONS: Opt[] = [opt('Yes', 'Borrower-Paid'), opt('No', 'None/LPMI')];
export const CITIZENSHIPS: Opt[] = [opt('USCitizen', 'U.S. Citizen'), opt('PermResidentAlien', 'Permanent Resident'), opt('NonPermResidentAlien', 'Non-Perm Resident'), opt('ForeignNational', 'Foreign National')];
export const UNITS: Opt[] = [opt('OneUnit', '1 Unit'), opt('TwoUnits', '2 Units'), opt('ThreeUnits', '3 Units'), opt('FourUnits', '4 Units')];
export const PREPAYMENT_PENALTIES: Opt[] = [opt('None'), opt('OneYear', '1 yr'), opt('TwoYear', '2 yr'), opt('ThreeYear', '3 yr'), opt('FourYear', '4 yr'), opt('FiveYear', '5 yr')];
export const VETERAN_TYPES: Opt[] = [opt('ActiveDuty', 'Active Duty'), opt('NationalGuardOrReserves', 'Guard/Reserves')];
export const INCOME_VERIFICATION_TYPES: Opt[] = [
  opt('FullDoc', 'Full Doc'), opt('PersonalBankStmt12Mos', 'Personal Bank Stmt · 12mo'), opt('PersonalBankStmt24Mos', 'Personal Bank Stmt · 24mo'),
  opt('BusinessBankStmt12Mos', 'Business Bank Stmt · 12mo'), opt('BusinessBankStmt24Mos', 'Business Bank Stmt · 24mo'), opt('OneYearAltDoc', '1yr Alt Doc'),
  opt('TwoYearAltDoc', '2yr Alt Doc'), opt('AssetRelated', 'Asset Related'), opt('InvestorDscr', 'DSCR'), opt('RestrictedStock', 'Restricted Stock'),
  opt('InvestorNoRatio', 'No Ratio'), opt('WrittenVOE', 'Written VOE'), opt('Stated', 'Stated'), opt('OneYear1099', '1yr 1099'), opt('TwoYear1099', '2yr 1099'),
  opt('PLOneYear', 'P&L 1yr'), opt('PLTwoYear', 'P&L 2yr'), opt('NoIncomeVerification', 'No Income Verification')
];
export const HOUSING_EVENTS: Opt[] = [opt('None'), opt('Foreclosure'), opt('ShortSale', 'Short Sale'), opt('DeedInLieu', 'Deed-in-Lieu'), opt('Modification'), opt('Forbearance')];
export const EVENT_SEASONINGS: Opt[] = [
  opt('Last1To12Months', '≤ 12 mo'), opt('Last13To24Months', '13–24 mo'), opt('Last25To36Months', '25–36 mo'),
  opt('Last37To48Months', '37–48 mo'), opt('Last49To60Months', '49–60 mo'), opt('MoreThanFiveYearsAgo', '5+ yr')
];
export const BANKRUPTCY_TYPES: Opt[] = [opt('None'), opt('Chapter7', 'Chapter 7'), opt('Chapter11', 'Chapter 11'), opt('Chapter13', 'Chapter 13')];
export const BANKRUPTCY_OUTCOMES: Opt[] = [opt('Discharged'), opt('Dismissed'), opt('OpenOrPending', 'Open/Pending')];
export const MORTGAGE_LATES: Opt[] = [opt('0x30', 'Clean'), opt('1x30', '1×30'), opt('2x30', '2×30'), opt('1x60', '1×60'), opt('1x90', '1×90')];
